/**
 * Analytics + crash reporting — one thin, vendor-agnostic seam the rest of the app talks to.
 *
 *   track(id, value?)  → a GameAnalytics "design event" (funnels, retention, engagement).
 *   captureError(err)  → a Sentry exception (Sentry also auto-captures unhandled errors/rejections).
 *
 * GameAnalytics ships an IIFE browser SDK that binds `window.GameAnalytics` (loaded as a classic
 * <script> in index.html — it can't go through the bundler). We drive it via its command-queue API
 * so call timing/order never matters. Both are no-ops until their keys are present (VITE_* env), so
 * the game runs identically with analytics off (e.g. local dev without a .env).
 */
import * as Sentry from '@sentry/browser';

type GACommand = (command: string, ...args: unknown[]) => void;

declare global {
  interface Window {
    GameAnalytics?: GACommand;
  }
}

const GA_GAME_KEY = import.meta.env.VITE_GA_GAME_KEY ?? '';
const GA_SECRET_KEY = import.meta.env.VITE_GA_SECRET_KEY ?? '';
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN ?? '';
const BUILD = 'web 0.1.0';

let gaReady = false;

/** Boot analytics + crash reporting once, at app start. Safe to call when keys are absent. */
export function initAnalytics(): void {
  if (SENTRY_DSN) {
    try {
      Sentry.init({ dsn: SENTRY_DSN, tracesSampleRate: 0 });
    } catch {
      /* never let telemetry setup break the app */
    }
  }

  const ga = typeof window !== 'undefined' ? window.GameAnalytics : undefined;
  if (ga && GA_GAME_KEY && GA_SECRET_KEY) {
    try {
      ga('setEnabledInfoLog', false);
      ga('configureBuild', BUILD); // must precede initialize
      ga('initialize', GA_GAME_KEY, GA_SECRET_KEY);
      gaReady = true;
    } catch {
      /* SDK missing/blocked → stay a no-op */
    }
  }
}

/**
 * Record a design event. `id` is a colon-delimited hierarchy GameAnalytics charts as a funnel,
 * e.g. `match:start:online` or `match:end:online:win`. Optional numeric `value` (e.g. a score).
 */
export function track(id: string, value?: number): void {
  if (!gaReady || !window.GameAnalytics) return;
  try {
    if (value === undefined) window.GameAnalytics('addDesignEvent', id);
    else window.GameAnalytics('addDesignEvent', id, value);
  } catch {
    /* ignore */
  }
}

/** Report a handled error to Sentry (unhandled ones are captured automatically once init'd). */
export function captureError(err: unknown): void {
  if (!SENTRY_DSN) return;
  try {
    Sentry.captureException(err);
  } catch {
    /* ignore */
  }
}
